import { Children, cloneElement, forwardRef, isValidElement } from 'react'
import type { CSSProperties, ComponentPropsWithoutRef, ElementType, PointerEvent as ReactPointerEvent, ReactElement, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Button as BaseButton } from '@base-ui/react/button'
import { Field as BaseField } from '@base-ui/react/field'
import { Progress as BaseProgress } from '@base-ui/react/progress'

/*
 * Presentational pieces MUI used to supply. Base UI is deliberately headless
 * and ships no Card/Typography/Chip/Alert, so these are plain semantic elements
 * over the classes in ui.css.
 */

/* ------------------------------------------------------------------ button */

type ButtonVariant = 'contained' | 'outlined' | 'text'

export interface ButtonProps extends Omit<ComponentPropsWithoutRef<'button'>, 'color'> {
  variant?: ButtonVariant
  color?: 'primary' | 'secondary' | 'error' | 'inherit'
  fullWidth?: boolean
  size?: 'small' | 'medium' | 'large'
  startIcon?: ReactNode
  endIcon?: ReactNode
  to?: string
  href?: string
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'text',
    color = 'primary',
    fullWidth,
    size,
    startIcon,
    endIcon,
    className = '',
    children,
    to,
    href,
    ...props
  },
  ref
) {
  const classes = [
    'ui-button',
    variant !== 'contained' && `ui-button--${variant}`,
    color === 'error' && 'ui-button--error',
    size === 'small' && 'ui-button--small',
    fullWidth && 'ui-button--full',
    className
  ]
    .filter(Boolean)
    .join(' ')

  const content = (
    <>
      {startIcon}
      {children}
      {endIcon}
    </>
  )

  if (to) {
    return (
      <Link to={to} className={classes} {...(props as any)}>
        {content}
      </Link>
    )
  }
  if (href) {
    return (
      <a href={href} className={classes} {...(props as any)}>
        {content}
      </a>
    )
  }

  return (
    <BaseButton ref={ref} type="button" className={classes} {...props}>
      {content}
    </BaseButton>
  )
})

export const IconButton = forwardRef<
  HTMLButtonElement,
  Omit<ComponentPropsWithoutRef<'button'>, 'color'> & { color?: string; size?: string; href?: string; edge?: string }
>(function IconButton({ className = '', color: _color, size: _size, edge: _edge, href, ...props }, ref) {
  if (href) {
    return <a href={href} className={`ui-icon-button ${className}`} {...(props as any)} />
  }
  return <button ref={ref} type="button" className={`ui-icon-button ${className}`} {...props} />
})

/* -------------------------------------------------------------------- card */

export const Card = ({
  className = '',
  variant,
  ...props
}: ComponentPropsWithoutRef<'div'> & { variant?: string }) => (
  <div
    className={`ui-card ${variant === 'outlined' ? 'ui-card--outlined' : ''} ${className}`}
    {...props}
  />
)

export const CardContent = ({ className = '', ...props }: ComponentPropsWithoutRef<'div'>) => (
  <div className={`ui-card__content ${className}`} {...props} />
)

export const CardActions = ({ className = '', ...props }: ComponentPropsWithoutRef<'div'>) => (
  <div className={`ui-card__actions ${className}`} {...props} />
)

export const CardActionArea = ({
  className = '',
  to,
  href,
  ...props
}: ComponentPropsWithoutRef<'button'> & { to?: string; href?: string }) => {
  if (to) {
    return <Link to={to} className={`ui-card__action-area ${className}`} {...(props as any)} />
  }
  if (href) {
    return <a href={href} className={`ui-card__action-area ${className}`} {...(props as any)} />
  }
  return <button type="button" className={`ui-card__action-area ${className}`} {...props} />
}

export interface CardMediaProps extends Omit<ComponentPropsWithoutRef<'img'>, 'height'> {
  image?: string
  component?: ElementType
  height?: string | number
}

export const CardMedia = ({
  image,
  src,
  className = '',
  component,
  height,
  style,
  alt,
  ...props
}: CardMediaProps) => {
  const sized = height === undefined ? style : { height, ...style }

  // An explicit <img> when the call site asked for one, or when only `src` is
  // given. Otherwise a background-image box, which is what MUI did by default
  // and which simply shows nothing while the artwork is still being fetched.
  if (component === 'img' || (src && !image)) {
    return (
      <img
        className={`ui-card__media ${className}`}
        src={image ?? src}
        alt={alt ?? ''}
        style={sized}
        {...props}
      />
    )
  }

  return (
    <div
      className={`ui-card__media ui-card__media--bg ${className}`}
      role={image ? 'img' : undefined}
      aria-label={alt || undefined}
      style={image ? { ...sized, backgroundImage: `url(${image})` } : sized}
      {...(props as ComponentPropsWithoutRef<'div'>)}
    />
  )
}

export interface LinkTextProps extends Omit<ComponentPropsWithoutRef<'a'>, 'color'> {
  to?: string
  underline?: string
  color?: string
  variant?: string
}

/* MUI's <Link> resolved `href` through react-router; `to` does that directly. */
export const LinkText = ({ to, href, className = '', underline: _u, color: _c, variant: _v, ...props }: LinkTextProps) => {
  if (to) return <Link to={to} className={`ui-link ${className}`} {...(props as any)} />
  return <a href={href} className={`ui-link ${className}`} {...props} />
}

export { LinkText as Link }

export const Paper = ({
  className = '',
  elevation: _elevation,
  ...props
}: ComponentPropsWithoutRef<'div'> & { elevation?: number }) => (
  <div className={`ui-paper ${className}`} {...props} />
)

/* -------------------------------------------------------------- typography */

const VARIANT_TAG: Record<string, ElementType> = {
  h1: 'h1', h2: 'h2', h3: 'h3', h4: 'h4', h5: 'h5', h6: 'h6',
  body1: 'p', body2: 'p', caption: 'span', subtitle1: 'p', subtitle2: 'p'
}

export interface TypographyProps extends ComponentPropsWithoutRef<'p'> {
  variant?: keyof typeof VARIANT_TAG
  color?: string
  component?: ElementType
  gutterBottom?: boolean
  noWrap?: boolean
}

export const Typography = ({
  variant = 'body1',
  component,
  gutterBottom,
  noWrap,
  color,
  className = '',
  style,
  ...props
}: TypographyProps) => {
  const Tag = (component ?? VARIANT_TAG[variant] ?? 'p') as ElementType
  const classes = [
    'ui-text',
    `ui-text--${variant}`,
    gutterBottom && 'ui-text--gutter',
    color?.includes('secondary') && 'ui-text--secondary',
    className
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <Tag
      className={classes}
      style={noWrap ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', ...style } : style}
      {...props}
    />
  )
}

/* -------------------------------------------------------------------- chip */

export interface ChipProps extends Omit<ComponentPropsWithoutRef<'span'>, 'color'> {
  label?: ReactNode
  color?: 'default' | 'primary' | 'success' | 'warning' | 'error'
  size?: 'small' | 'medium'
  icon?: ReactNode
}

export const Chip = ({ label, color = 'default', size, icon, className = '', children, ...props }: ChipProps) => (
  <span
    className={[
      'ui-chip',
      color !== 'default' && `ui-chip--${color}`,
      size === 'small' && 'ui-chip--small',
      props.onClick && 'ui-chip--clickable',
      className
    ]
      .filter(Boolean)
      .join(' ')}
    {...props}
  >
    {icon}
    {label ?? children}
  </span>
)

/* ------------------------------------------------------------------- alert */

export interface AlertProps extends ComponentPropsWithoutRef<'div'> {
  severity?: 'error' | 'success' | 'info' | 'warning'
  icon?: ReactNode
}

export const Alert = ({ severity = 'info', icon, className = '', children, ...props }: AlertProps) => (
  <div className={`ui-alert ui-alert--${severity} ${className}`} role="alert" {...props}>
    {icon ? <span className="ui-alert__icon">{icon}</span> : null}
    <div>{children}</div>
  </div>
)

export const AlertTitle = ({ className = '', ...props }: ComponentPropsWithoutRef<'div'>) => (
  <div className={`ui-alert__title ${className}`} {...props} />
)

/* -------------------------------------------------------------------- list */

export const List = ({ className = '', ...props }: ComponentPropsWithoutRef<'ul'>) => (
  <ul className={`ui-list ${className}`} {...props} />
)

export const ListItem = ({
  className = '',
  disablePadding: _disablePadding,
  secondaryAction,
  children,
  ...props
}: ComponentPropsWithoutRef<'li'> & { disablePadding?: boolean; secondaryAction?: ReactNode }) => (
  <li className={`ui-list-item ${className}`} {...props}>
    {children}
    {secondaryAction ? <span className="ui-list-item__action">{secondaryAction}</span> : null}
  </li>
)

function spawnRipple(event: ReactPointerEvent<HTMLElement>) {
  const el = event.currentTarget
  const rect = el.getBoundingClientRect()
  const size = Math.max(rect.width, rect.height) * 2
  const ripple = document.createElement('span')
  ripple.className = 'ui-ripple'
  ripple.style.width = `${size}px`
  ripple.style.height = `${size}px`
  ripple.style.left = `${event.clientX - rect.left - size / 2}px`
  ripple.style.top = `${event.clientY - rect.top - size / 2}px`
  el.appendChild(ripple)
  ripple.addEventListener('animationend', () => ripple.remove())
}

export const ListItemButton = ({
  className = '',
  to,
  onPointerDown,
  ...props
}: ComponentPropsWithoutRef<'button'> & { to?: string }) => {
  const ripple = (event: ReactPointerEvent<HTMLElement>) => {
    spawnRipple(event)
    onPointerDown?.(event as ReactPointerEvent<HTMLButtonElement>)
  }

  if (to) {
    return <Link to={to} className={`ui-list-item-button ${className}`} onPointerDown={ripple} {...(props as any)} />
  }
  return <button type="button" className={`ui-list-item-button ${className}`} onPointerDown={ripple} {...props} />
}

export const ListItemIcon = ({ className = '', ...props }: ComponentPropsWithoutRef<'span'>) => (
  <span className={`ui-list-item-icon ${className}`} {...props} />
)

export interface ListItemTextProps extends ComponentPropsWithoutRef<'span'> {
  primary?: ReactNode
  secondary?: ReactNode
}

export const ListItemText = ({ primary, secondary, className = '', children, ...props }: ListItemTextProps) => (
  <span className={className} {...props}>
    <span className="ui-list-item-text__primary">{primary ?? children}</span>
    {secondary ? <span className="ui-list-item-text__secondary">{secondary}</span> : null}
  </span>
)

/* ---------------------------------------------------------------- progress */

export interface LinearProgressProps extends ComponentPropsWithoutRef<'div'> {
  value?: number
  variant?: 'determinate' | 'indeterminate'
}

export const LinearProgress = ({
  value,
  variant = value === undefined ? 'indeterminate' : 'determinate',
  className = ''
}: LinearProgressProps) => (
  <BaseProgress.Root value={variant === 'determinate' ? (value ?? 0) : null}>
    <BaseProgress.Track
      className={[
        'ui-progress',
        variant === 'indeterminate' && 'ui-progress--indeterminate',
        className
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <BaseProgress.Indicator className="ui-progress__bar" />
    </BaseProgress.Track>
  </BaseProgress.Root>
)

/* ----------------------------------------------------------------- stepper */

export const Stepper = ({
  activeStep = 0,
  children,
  className = '',
  ...props
}: ComponentPropsWithoutRef<'div'> & { activeStep?: number }) => (
  <div className={`ui-stepper ${className}`} data-active-step={activeStep} {...props}>
    {Children.map(children, (child, position) => {
      if (!isValidElement(child) || child.type !== Step) return child
      /*
       * Steps used to render "(index ?? 0) + 1", and no consumer passed an
       * index, so every dot read "1". The position in the Stepper *is* the
       * index, so inject it -- and the active flag, which lets a Stepper
       * show progress -- unless the consumer set them explicitly.
       */
      const stepProps = child.props as { index?: number; active?: boolean }
      return cloneElement(child as ReactElement<{ index?: number; active?: boolean }>, {
        index: stepProps.index ?? position,
        active: stepProps.active ?? position <= activeStep
      })
    })}
  </div>
)

export const Step = ({
  active,
  index,
  children,
  className = '',
  ...props
}: ComponentPropsWithoutRef<'div'> & { active?: boolean; index?: number }) => (
  <div className={`ui-step ${active ? 'ui-step--active' : ''} ${className}`} {...props}>
    <span className="ui-step__dot">{(index ?? 0) + 1}</span>
    {children}
  </div>
)

export const StepLabel = ({ className = '', ...props }: ComponentPropsWithoutRef<'span'>) => (
  <span className={`ui-step__label ${className}`} {...props} />
)

/* -------------------------------------------------------------------- grid */

export interface GridProps extends ComponentPropsWithoutRef<'div'> {
  container?: boolean
  spacing?: number
  size?: { xs?: number; sm?: number; md?: number; lg?: number } | number
}

/*
 * MUI's 12-column Grid, as CSS grid. `container` lays out the tracks; `size`
 * on a child is the column span at each breakpoint, expressed as custom
 * properties that ui.css turns into `grid-column` inside media queries.
 */
export const Grid = ({ container, spacing = 0, size, className = '', style, ...props }: GridProps) => {
  if (container) {
    return (
      <div
        className={`ui-grid ${className}`}
        style={{ gap: `${spacing * 8}px`, ...style }}
        {...props}
      />
    )
  }

  const spans = typeof size === 'number' ? { xs: size } : (size ?? {})

  return (
    <div
      className={`ui-grid__item ${className}`}
      style={
        {
          '--span-xs': spans.xs ?? 12,
          '--span-sm': spans.sm ?? spans.xs ?? 12,
          '--span-md': spans.md ?? spans.sm ?? spans.xs ?? 12,
          '--span-lg': spans.lg ?? spans.md ?? spans.sm ?? spans.xs ?? 12,
          ...style
        } as CSSProperties
      }
      {...props}
    />
  )
}

/* --------------------------------------------------------------- textfield */

export interface TextFieldProps extends Omit<ComponentPropsWithoutRef<'input'>, 'size'> {
  label?: ReactNode
}

export const TextField = ({ label, placeholder, id, className = '', ...props }: TextFieldProps) => (
  <BaseField.Root className={`ui-field ${className}`}>
    {label ? <BaseField.Label className="ui-field__label">{label}</BaseField.Label> : null}
    {placeholder ? (
      <span className="ui-textfield">
        <BaseField.Control id={id} className="ui-input ui-textfield__input" placeholder={placeholder} {...props} />
        <span className="ui-textfield__label" aria-hidden="true">{placeholder}</span>
      </span>
    ) : (
      <BaseField.Control id={id} className="ui-input" {...props} />
    )}
  </BaseField.Root>
)

export interface TextAreaProps extends ComponentPropsWithoutRef<'textarea'> {
  label?: ReactNode
}

export const TextArea = ({ label, placeholder, id, className = '', ...props }: TextAreaProps) => (
  <BaseField.Root className={`ui-field ${className}`}>
    {label ? <BaseField.Label className="ui-field__label">{label}</BaseField.Label> : null}
    {placeholder ? (
      <span className="ui-textfield ui-textfield--area">
        <BaseField.Control render={<textarea />} id={id} className="ui-input ui-textfield__input" placeholder={placeholder} {...(props as any)} />
        <span className="ui-textfield__label" aria-hidden="true">{placeholder}</span>
      </span>
    ) : (
      <BaseField.Control render={<textarea />} id={id} className="ui-input" {...(props as any)} />
    )}
  </BaseField.Root>
)

/* ------------------------------------------------------- form control label */

export const FormControlLabel = ({
  control,
  label,
  className = ''
}: {
  control: ReactNode
  label: ReactNode
  className?: string
}) => (
  <label className={`ui-radio-label ${className}`}>
    {control}
    {label}
  </label>
)
